import { useCallback, memo, type ReactNode } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@shared/components/ui/dialog'
import { Button } from '@shared/components/ui/button'
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  ShieldAlert,
  Loader2,
  type LucideIcon,
} from 'lucide-react'

// ── Variant config ──────────────────────────────────────────────

type DialogVariant = 'info' | 'warning' | 'destructive' | 'success'

interface VariantConfig {
  icon: LucideIcon
  iconWrapperClass: string
  confirmVariant: 'default' | 'destructive' | 'outline'
}

const VARIANT_CONFIG: Record<DialogVariant, VariantConfig> = {
  info: {
    icon: Info,
    iconWrapperClass: 'bg-primary/10 text-primary',
    confirmVariant: 'default',
  },
  warning: {
    icon: AlertTriangle,
    iconWrapperClass: 'bg-amber-50 text-amber-600',
    confirmVariant: 'default',
  },
  destructive: {
    icon: ShieldAlert,
    iconWrapperClass: 'bg-destructive/10 text-destructive',
    confirmVariant: 'destructive',
  },
  success: {
    icon: CheckCircle2,
    iconWrapperClass: 'bg-emerald-50 text-emerald-600',
    confirmVariant: 'default',
  },
}

// ── Props ───────────────────────────────────────────────────────

export interface ConfirmationDialogProps {
  /** Controls visibility. */
  isOpen: boolean
  /** Called when the dialog requests to close (overlay click, X, Cancel). */
  onClose: () => void
  /** Called when the user clicks the confirm button. Can be async -- the
   *  dialog will show a spinner until the promise settles. */
  onConfirm: () => void | Promise<void>

  /** Visual variant that determines icon, colors, and confirm button style. */
  variant?: DialogVariant
  /** Override the default variant icon. */
  icon?: LucideIcon
  /** Dialog heading. */
  title: string
  /** Supporting copy below the title. Can be a string or JSX. */
  description?: ReactNode
  /** Label for the confirm button. Defaults to "Confirm". */
  confirmLabel?: string
  /** Label for the cancel button. Defaults to "Cancel". */
  cancelLabel?: string
  /** When true the confirm button shows a spinner and both buttons are disabled. */
  isLoading?: boolean
  /** Hides the cancel button entirely (e.g. for acknowledgement-only dialogs). */
  hideCancelButton?: boolean
  /** Extra content rendered between the description and the footer (e.g. a
   *  checkbox or input for "type DELETE to confirm"). */
  children?: ReactNode
}

// ── Component ───────────────────────────────────────────────────

export const ConfirmationDialog = memo(function ConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  variant = 'info',
  icon: IconOverride,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  isLoading = false,
  hideCancelButton = false,
  children,
}: ConfirmationDialogProps) {
  const config = VARIANT_CONFIG[variant]
  const Icon = IconOverride ?? config.icon

  const handleConfirm = useCallback(async () => {
    await onConfirm()
  }, [onConfirm])

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[500px]" overlayClassName="bg-foreground opacity-80">
        <DialogHeader className="items-center text-center">
          {/* Large icon */}
          <div className={`flex justify-center mb-6 ${config.iconWrapperClass} bg-transparent`}>
            <Icon className="w-16 h-16" strokeWidth={0.75} />
          </div>

          <DialogTitle className="text-2xl font-medium text-slate-900">{title}</DialogTitle>

          {description && (
            <DialogDescription className="text-sm text-slate-500">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>

        {/* Optional slot for extra content */}
        {children && <div className="py-2">{children}</div>}

        <div className="flex flex-row gap-3 justify-center">
          {!hideCancelButton && (
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
              className="rounded-full h-12 font-medium bg-background border-foreground text-foreground px-6 hover:bg-muted"
            >
              {cancelLabel}
            </Button>
          )}

          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            className="rounded-full h-12 font-medium bg-foreground hover:bg-foreground/90 text-background px-6 shadow-lg"
          >
            {isLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
})
